import React from 'react';
import { Building, Clock, Users, Plug, AlertTriangle, X } from 'lucide-react';
import { useBusinessSettings, BusinessSettingsSection } from '@/context/BusinessSettingsContext';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroupLabel,
  useSidebar
} from '@/components/ui/sidebar';
import { cn } from '@/lib/core/utils';
import { Button } from '@/components/ui/button';

type NavItem = {
  id: BusinessSettingsSection;
  label: string;
  icon: React.ElementType;
  tooltip?: string;
};

const businessSettingsNavigation: NavItem[] = [
  {
    id: 'business-info',
    label: 'Business Information',
    icon: Building,
    tooltip: 'Basic details, hours, and policies'
  },
  {
    id: 'time-tracking',
    label: 'Time Tracking',
    icon: Clock,
    tooltip: 'Attendance and time tracking settings'
  },
  {
    id: 'employee-shifts',
    label: 'Employee & Shifts',
    icon: Users,
    tooltip: 'Shift assignment and kiosk settings'
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: Plug,
    tooltip: 'External system connections'
  },
  {
    id: 'account',
    label: 'Account & Danger Zone',
    icon: AlertTriangle,
    tooltip: 'Account info and destructive actions'
  }
];

export const BusinessSettingsSidebarNav = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  const { activeSection, setActiveSection, closeBusinessSettings } = useBusinessSettings();
  const { isMobile, setOpenMobile } = useSidebar();

  const handleItemClick = (item: NavItem) => {
    setActiveSection(item.id);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleClose = () => {
    closeBusinessSettings();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <div
      ref={ref}
      data-sidebar="navigation"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    >
      <SidebarGroup>
        <SidebarGroupLabel className="flex items-center justify-between pr-2">
          <span>Business Settings</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={handleClose}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Close settings</span>
          </Button>
        </SidebarGroupLabel>

        <SidebarMenu>
          {businessSettingsNavigation.map((item) => (
            <SidebarMenuItem key={item.id}>
              <SidebarMenuButton
                isActive={activeSection === item.id}
                tooltip={item.tooltip || item.label}
                onClick={() => handleItemClick(item)}
              >
                <item.icon />
                <span>{item.label}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </div>
  );
});

BusinessSettingsSidebarNav.displayName = 'BusinessSettingsSidebarNav';
